// ============================================================
// WirelessCommunication.jsx
//
// Course content page for "Wireless Communication" (IoT track).
// Rewritten to:
//   1) remove six dead imports (FaMobileAlt, FaTerminal, FaSignal,
//      FaRoute, SiArduino, SiEspressif — none were ever rendered),
//   2) fix a reference to BRAND.emerald, a token that didn't exist
//      in this file — it silently fell back to a hardcoded hex via
//      `||` every time, which worked by accident,
//   3) soften the LoRa "15km" range claim to "up to 15km (line of
//      sight)" — that figure is a best-case ceiling, not typical,
//   4) match the app-wide dark-purple/goldenrod look,
//   5) use arrow-function components and Material Icons only.
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Paper,
    Stack,
    IconButton,
    Avatar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";

// ── Icons — @mui/icons-material only ──
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import WifiIcon from "@mui/icons-material/Wifi";
import BluetoothIcon from "@mui/icons-material/Bluetooth";
import HubIcon from "@mui/icons-material/Hub";
import CellTowerIcon from "@mui/icons-material/CellTower";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import SettingsInputAntennaIcon from "@mui/icons-material/SettingsInputAntenna";

// ── Brand tokens (matches IoTDashboard.jsx / SensorsActuators.jsx) ──
const BRAND = {
    bg: "#100720",
    surface: "rgba(16,7,32,0.85)",
    border: "rgba(255,255,255,0.1)",
    gold: "goldenrod",
    teal: "#2DD4BF",
    white: "#FAFAF9",
    muted: "rgba(255,255,255,0.65)",
    codeBg: "#0b0515",
};

const PROTOCOLS = [
    { title: "Wi-Fi", icon: <WifiIcon />, range: "~100m", power: "High", useCase: "ESP32 web servers and video streaming.", color: BRAND.gold },
    { title: "BLE", icon: <BluetoothIcon />, range: "~10m", power: "Low", useCase: "Wearables and point-to-point mobile apps.", color: BRAND.teal },
    { title: "Zigbee", icon: <HubIcon />, range: "~20m per hop (mesh extends further)", power: "Ultra-Low", useCase: "Smart home mesh networking (lights, plugs).", color: BRAND.gold },
    { title: "LoRa", icon: <CellTowerIcon />, range: "Up to 15km (line of sight)", power: "Ultra-Low", useCase: "Rural sensors and remote agriculture.", color: BRAND.teal },
];

const COMPARISON_ROWS = [
    { name: "Wi-Fi", topo: "Star", freq: "2.4 / 5 GHz", use: "High-bandwidth apps" },
    { name: "BLE", topo: "P2P / Mesh", freq: "2.4 GHz", use: "Personal devices" },
    { name: "Zigbee", topo: "Mesh", freq: "2.4 GHz", use: "Home automation" },
    { name: "LoRa", topo: "Star-of-stars", freq: "Sub-GHz", use: "Rural monitoring" },
];

// Sub-components (arrow-function style, defined before first use)

const ProtocolCard = ({ title, icon, range, power, useCase, color }) => (
    <Paper
        elevation={0}
        sx={{
            p: 3,
            background: BRAND.surface,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 4,
            transition: "0.3s",
            height: "100%",
            "&:hover": { transform: "translateY(-5px)", borderColor: color },
        }}
    >
        <Stack spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar sx={{ bgcolor: `${color}26`, color }}>{icon}</Avatar>
                <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700 }}>{title}</Typography>
            </Box>
            <Box>
                <Typography variant="caption" sx={{ color: BRAND.muted, display: "block" }}>Range: {range}</Typography>
                <Typography variant="caption" sx={{ color: BRAND.muted, display: "block" }}>Power: {power}</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: BRAND.white, fontSize: 13 }}>{useCase}</Typography>
        </Stack>
    </Paper>
);

const TerminalBlock = ({ code, label }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API isn't available in every browser/context.
        }
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.03)" }}>
                <Typography variant="caption" sx={{ color: BRAND.gold, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Typography component="pre" sx={{ p: 2.5, color: "#FCD34D", fontSize: 13, fontFamily: "'Fira Code', monospace", overflowX: "auto" }}>
                {code}
            </Typography>
        </Box>
    );
};

// ── Main Page ───────────────────────────────────────────────────

const WirelessCommunication = () => (
    <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
        <Container maxWidth="lg">
            {/* HERO SECTION */}
            <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                <Chip
                    icon={<SchoolIcon style={{ color: BRAND.gold }} />}
                    label="Module 04: IoT Connectivity"
                    sx={{ bgcolor: "rgba(218,165,32,0.1)", color: BRAND.gold, border: `1px solid ${BRAND.border}`, mb: 2 }}
                />
                <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                    Wireless <span style={{ color: BRAND.teal }}>Communication</span>
                </Typography>
                <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                    IoT thrives on connectivity. Mastering wireless protocols means understanding the trade-off
                    between data rate, range, and battery life.
                </Typography>
            </Box>

            {/* PROTOCOL GRID */}
            <Box sx={{ mb: 10 }}>
                <Grid container spacing={3}>
                    {PROTOCOLS.map((p) => (
                        <Grid item xs={12} sm={6} md={3} key={p.title}>
                            <ProtocolCard {...p} />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* IMPLEMENTATION STEPS */}
            <Box sx={{ mb: 10 }}>
                <Stack spacing={8}>
                    <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <Avatar sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 900 }}>1</Avatar>
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>Wi-Fi Provisioning (ESP32)</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, mb: 3 }}>
                            Using the ESP32's built-in radio to connect to local infrastructure. This enables
                            MQTT or HTTP communication.
                        </Typography>
                        <TerminalBlock
                            label="WIFI_CONNECT.ino"
                            code={`#include <WiFi.h>\n\nvoid setup() {\n  Serial.begin(115200);\n  WiFi.begin("SSID", "PASSWORD");\n  while (WiFi.status() != WL_CONNECTED) {\n    delay(500);\n  }\n  Serial.println("IP Address: " + WiFi.localIP().toString());\n}`}
                        />
                    </Box>

                    <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <Avatar sx={{ bgcolor: BRAND.teal, color: BRAND.bg, fontWeight: 900 }}>2</Avatar>
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>Bluetooth Low Energy (GATT)</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, mb: 3 }}>
                            BLE uses Services and Characteristics. Ideal for mobile-to-device interaction
                            without a router.
                        </Typography>
                        <TerminalBlock
                            label="BLE_ADVERTISE.ino"
                            code={`#include <BLEDevice.h>\n#include <BLEServer.h>\n\nvoid setup() {\n  BLEDevice::init("IoT_Node_01");\n  BLEServer *pServer = BLEDevice::createServer();\n  pServer->getAdvertising()->start();\n}\n\nvoid loop() {}`}
                        />
                    </Box>

                    <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <Avatar sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 900 }}>3</Avatar>
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>LoRa: Long-Range Modulation</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, mb: 3 }}>
                            Operates in the Sub-GHz spectrum (433MHz / 868MHz / 915MHz depending on region).
                            Perfect for sending small data packets over long distances at very low power.
                        </Typography>
                        <TerminalBlock
                            label="LORA_SENDER.ino"
                            code={`#include <LoRa.h>\n\nvoid setup() {\n  LoRa.begin(915E6); // Use the frequency allowed in your region\n  LoRa.beginPacket();\n  LoRa.print("Sensor_Update_OK");\n  LoRa.endPacket();\n}\n\nvoid loop() {}`}
                        />
                    </Box>
                </Stack>
            </Box>

            {/* COMPARISON TABLE */}
            <Box sx={{ mb: 10 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                    <CompareArrowsIcon sx={{ fontSize: 32, color: BRAND.gold }} />
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>Protocol Comparison Matrix</Typography>
                </Box>
                <TableContainer component={Paper} sx={{ bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                    <Table>
                        <TableHead sx={{ bgcolor: "rgba(255,255,255,0.05)" }}>
                            <TableRow>
                                <TableCell sx={{ color: BRAND.gold, fontWeight: 700 }}>Protocol</TableCell>
                                <TableCell sx={{ color: BRAND.white }}>Topology</TableCell>
                                <TableCell sx={{ color: BRAND.white }}>Frequency</TableCell>
                                <TableCell sx={{ color: BRAND.white }}>Best For</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {COMPARISON_ROWS.map((row) => (
                                <TableRow key={row.name}>
                                    <TableCell sx={{ color: BRAND.white, fontWeight: 700 }}>{row.name}</TableCell>
                                    <TableCell sx={{ color: BRAND.muted }}>{row.topo}</TableCell>
                                    <TableCell sx={{ color: BRAND.muted }}>{row.freq}</TableCell>
                                    <TableCell sx={{ color: BRAND.muted }}>{row.use}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* FOOTER CTA */}
            <Box sx={{ p: 8, borderRadius: 8, textAlign: "center", background: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}` }}>
                <SettingsInputAntennaIcon sx={{ fontSize: 40, color: BRAND.gold, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Broadcasting Ready?</Typography>
                <Typography sx={{ color: BRAND.muted, mb: 4 }}>Select the right protocol for your environment and start transmitting.</Typography>
                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
                    <Chip label="Launch Wireless Lab" onClick={() => {}} sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 800, px: 3 }} />
                    <Chip label="Antenna Design Guide" variant="outlined" onClick={() => {}} sx={{ borderColor: BRAND.muted, color: BRAND.white }} />
                </Stack>
            </Box>
        </Container>
    </Box>
);

export default WirelessCommunication;
